import type { ReactNode } from 'react';
import { useMe } from '@features/user-profile/hooks/useMe';
import { BrandLoader } from '@shared/ui';
import { PendingApprovalPage } from './PendingApprovalPage';

interface ApprovalGuardProps {
  children: ReactNode;
}

export function ApprovalGuard({ children }: ApprovalGuardProps) {
  const me = useMe();

  if (me.isPending) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-canvas text-text">
        <BrandLoader decorative />
        <p className="text-neutral-500">Signing you in…</p>
      </div>
    );
  }

  // Connectivity failure (client timeout / transport error) — show a recoverable screen rather than
  // silently rendering the app against an unreachable backend (or, pre-fix, hanging on the splash).
  const errorKind = (me.error as { kind?: string } | null)?.kind;
  if (me.isError && (errorKind === 'timeout' || errorKind === 'network')) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-canvas text-text">
        <p className="text-neutral-500">We can’t reach the server right now.</p>
        <button
          type="button"
          onClick={() => void me.refetch()}
          className="rounded-md border border-neutral-300 px-4 py-2 text-sm text-text hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800"
        >
          Try again
        </button>
      </div>
    );
  }

  const status = me.data?.approvalStatus;
  if (status === 'pending' || status === 'rejected') {
    return <PendingApprovalPage status={status} />;
  }

  // approved (or, defensively, an errored /me — let the app render; the BE gate still protects data).
  return <>{children}</>;
}
